import { useEffect, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { QRCodeSVG } from 'qrcode.react';
import { AlertTriangle, Printer } from 'lucide-react';
import type { Tracker } from '@/api/trackers';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  LABEL_SIZES,
  LABEL_SIZE_STORAGE_KEY,
  labelLayout,
  labelPrintDocument,
  labelQrProps,
  labelSizeById,
  labelSizeName,
  logoBadgeDataUri,
  type LabelLayout,
  type LabelSize,
} from '@/lib/trackerLabel';
// Cópia de `docs/brand/windmaps_symbol.svg` (o canônico): editar lá e recopiar.
import symbolSvg from '@/assets/brand/windmaps_symbol.svg?raw';

/// px por mm a 96 dpi, só pra escalar a prévia na tela.
const PX_PER_MM = 96 / 25.4;
/// Largura máxima da prévia em px: grande o bastante pra conferir o IMEI.
const PREVIEW_MAX_PX = 320;
/// Resolução interna do QR. O tamanho real vem do estilo em mm.
const QR_RENDER_PX = 256;

function readStoredSize(): LabelSize {
  try {
    return labelSizeById(window.localStorage.getItem(LABEL_SIZE_STORAGE_KEY));
  } catch {
    return labelSizeById(null);
  }
}

function storeSize(id: string) {
  try {
    window.localStorage.setItem(LABEL_SIZE_STORAGE_KEY, id);
  } catch {
    // Navegação privada / armazenamento bloqueado: só não lembra o tamanho.
  }
}

/// Modal da etiqueta adesiva: QR com o IMEI (selo da marca no centro) e o
/// botão de imprimir, pensado pra impressora térmica de etiquetas.
///
/// `imeiChanged`: a modal abriu sozinha depois de uma correção de IMEI, e o
/// adesivo já colado na caixa ficou errado.
export function TrackerLabelDialog({
  tracker,
  imeiChanged = false,
  onClose,
}: {
  tracker: Tracker;
  imeiChanged?: boolean;
  onClose: () => void;
}) {
  const [size, setSize] = useState<LabelSize>(readStoredSize);
  const [printError, setPrintError] = useState<string | null>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  // O iframe da última impressão. Só sai no `afterprint`, na impressão
  // seguinte ou quando a modal fecha: nunca por tempo, senão some com a janela
  // de impressão ainda aberta (Firefox/Safari não seguram o script no print()).
  const printFrame = useRef<HTMLIFrameElement | null>(null);
  useEffect(() => () => printFrame.current?.remove(), []);
  const badge = useMemo(() => logoBadgeDataUri(symbolSvg), []);
  const layout = labelLayout(size);

  const scale = Math.min(2.5, PREVIEW_MAX_PX / (size.widthMm * PX_PER_MM));

  function print() {
    const node = labelRef.current;
    if (!node) return;
    setPrintError(null);
    printFrame.current?.remove();
    try {
      printFrame.current = printHtml(labelPrintDocument(node.outerHTML, size, `Etiqueta IMEI ${tracker.imei}`));
    } catch (e) {
      console.error('[trackers] falha ao abrir a impressão da etiqueta', e);
      setPrintError('Não foi possível abrir a impressão. Tente novamente.');
    }
  }

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          className="
            fixed z-50 bg-background shadow-xl
            inset-x-0 top-0 rounded-b-xl pt-[env(safe-area-inset-top)]
            md:inset-auto md:left-1/2 md:top-[12%] md:-translate-x-1/2
            md:w-full md:max-w-md md:rounded-lg md:pt-0
            data-[state=open]:animate-in data-[state=open]:slide-in-from-top-4
          "
        >
          <div className="space-y-4 p-4">
            <div className="space-y-0.5">
              <Dialog.Title className="text-base font-semibold">Etiqueta do rastreador</Dialog.Title>
              <p className="text-sm text-muted-foreground">
                {tracker.manufacturer} · {tracker.model}
              </p>
            </div>

            {imeiChanged && (
              <p
                role="status"
                className="flex gap-2 rounded-md border border-amber-500/60 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-300"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                O IMEI mudou. Imprima a nova etiqueta e troque o adesivo da caixa.
              </p>
            )}

            <div className="flex justify-center rounded-md border bg-muted/40 p-4">
              {/* A caixa externa reserva o tamanho já escalado; a interna é a
                  etiqueta em mm de verdade, que também é o que vai pra
                  impressora. */}
              <div
                style={{
                  width: size.widthMm * PX_PER_MM * scale,
                  height: size.heightMm * PX_PER_MM * scale,
                }}
                className="shadow-sm ring-1 ring-black/10"
              >
                <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}>
                  <TrackerLabel
                    ref={labelRef}
                    tracker={tracker}
                    size={size}
                    layout={layout}
                    badge={badge}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="label-size">Tamanho da etiqueta</Label>
              <Select
                id="label-size"
                value={size.id}
                onChange={(e) => {
                  const next = labelSizeById(e.target.value);
                  setSize(next);
                  storeSize(next.id);
                }}
              >
                {LABEL_SIZES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {labelSizeName(s)}
                  </option>
                ))}
              </Select>
              <Dialog.Description className="text-xs text-muted-foreground">
                Na janela de impressão, escolha a impressora de etiquetas, o papel de{' '}
                {labelSizeName(size)} e margens “Nenhuma”.
              </Dialog.Description>
            </div>

            {printError && (
              <p className="text-sm text-destructive" role="alert">
                {printError}
              </p>
            )}

            <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
                Fechar
              </Button>
              <Button type="button" onClick={print} className="w-full sm:w-auto gap-1">
                <Printer className="h-4 w-4" /> Imprimir
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/// A etiqueta em si. Estilo todo inline e em mm: o `outerHTML` deste nó é o
/// que a impressora recebe, sem o CSS do painel.
function TrackerLabel({
  ref,
  tracker,
  size,
  layout,
  badge,
}: {
  ref: React.Ref<HTMLDivElement>;
  tracker: Tracker;
  size: LabelSize;
  layout: LabelLayout;
  badge: string;
}) {
  const logoPx = (QR_RENDER_PX * layout.logoMm) / layout.qrMm;

  // Só o QR, sem texto impresso (decisão de produto): o IMEI vai no QR, e o
  // `title` do SVG é só acessibilidade/tooltip, não sai no papel.
  return (
    <div
      ref={ref}
      data-testid="tracker-label"
      style={{
        width: `${size.widthMm}mm`,
        height: `${size.heightMm}mm`,
        boxSizing: 'border-box',
        padding: `${layout.paddingMm}mm`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
        overflow: 'hidden',
        pageBreakInside: 'avoid',
      }}
    >
      <QRCodeSVG
        {...labelQrProps(tracker.imei)}
        size={QR_RENDER_PX}
        bgColor="#ffffff"
        fgColor="#000000"
        title={`IMEI ${tracker.imei}`}
        imageSettings={{ src: badge, width: logoPx, height: logoPx, excavate: true }}
        style={{ width: `${layout.qrMm}mm`, height: `${layout.qrMm}mm`, flex: 'none', display: 'block' }}
      />
    </div>
  );
}

/// Imprime um documento HTML sem sair da tela: iframe invisível e `print()`
/// nele. Popup (`window.open`) seria barrado pelo bloqueador em alguns
/// navegadores e deixaria uma aba órfã. Devolve o iframe: ele some sozinho no
/// `afterprint`, e quem chama remove nos outros casos (navegador que não
/// dispara o evento).
function printHtml(html: string): HTMLIFrameElement {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.tabIndex = -1;
  Object.assign(iframe.style, {
    position: 'fixed',
    right: '0',
    bottom: '0',
    width: '0',
    height: '0',
    // Sem `visibility: hidden`: há navegador que imprime o iframe oculto em
    // branco. O tamanho zero já tira ele da tela.
    border: '0',
  });
  let removed = false;
  const cleanup = () => {
    if (removed) return;
    removed = true;
    iframe.remove();
  };
  iframe.onload = () => {
    const w = iframe.contentWindow;
    if (!w) {
      cleanup();
      console.error('[trackers] iframe de impressão sem contentWindow');
      return;
    }
    w.addEventListener('afterprint', cleanup);
    w.focus();
    w.print();
  };
  iframe.srcdoc = html;
  document.body.appendChild(iframe);
  return iframe;
}
